import { useMemo } from 'react';
import { startOfDay } from 'date-fns';
import { useSelector } from 'react-redux';
import { array, date, number, object, string, ref } from 'yup';

import { selectActiveTrainerList, selectLocationList } from '~/store/provider';
import { EventType } from '~/Models/DTO/Event.model';
import { getDefaultTime } from '~/Screens/Events/EventCreate/EventCreateSections/EventScheduleSection/EventScheduleTimeSeries/useDefaultTimeSeries';
import { ICalendarEvent } from '~/Screens/Calendar/interfaces';
import { getEventCategoryByType } from '~/Models/DTO/EventCategory.model';
import { useProvider } from '~/Hooks';
import { CategoryMap } from '~/Screens/Events/EventCreate/helpers';

export const useValidateEventCreation = ({ event }: { event: ICalendarEvent }) => {
  const locationList = useSelector(selectLocationList);
  const trainerList = useSelector(selectActiveTrainerList);
  const { currentProvider, isUsingThePartnerTool } = useProvider();

  return useMemo(() => {
    if (event.type === EventType.FREE_TRAINING) {
      const { start, end } = getDefaultTime({
        startDate: startOfDay(new Date(event.start)).toISOString(),
        eventType: event.type,
      });

      return {
        initialValues: {
          ...event,
          name: '',
          end,
          start,
          trainerIds: [],
          locationId: locationList?.[0]?.uuid,
        },
        validationSchema: object().shape({
          name: string().required(),
          locationId: string().required(),
          categoryId: number().required(),
        }),
      };
    }
    return {
      initialValues: {
        ...event,
        name: '',
        seats: 10,
        public: true,
        categoryId: isUsingThePartnerTool
          ? null
          : CategoryMap.get(`${currentProvider?.providerCategory?.id ?? 1}`),
        locationId: locationList?.[0]?.uuid,
        trainerIds: trainerList.length ? [trainerList?.[0]?.trainerId] : [],
        categoryType: getEventCategoryByType(event.type),
      },
      validationSchema: object().shape({
        name: string().required(),
        seats: number().min(1).required(),
        trainerIds: array().required(),
        locationId: string().required(),
        categoryId: number().required(),
        start: date()
          .required()
          .test(
            'validate_start_date',
            'start date cannot be in the past',
            (start: Date) => start > new Date(),
          ),
        end: date()
          .required()
          .min(ref('start'), 'the end date must be greater than the start date'),
      }),
    };
  }, [isUsingThePartnerTool]);
};
